import React from 'react';
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
} from '@headlessui/react';
import dayjs from 'dayjs';
import { DaySequence } from '@core/generated/gql-types';
import { useJobStore } from '@core/job/useJobStore';
import Button from '@/components/Button';
import { formatBreakTime, formatTimeRangeAndDuration } from '@/utils/utils';

interface DeleteTimesheetModalProps {
  isOpen: boolean;
  onClose: () => void;
  daySequence: DaySequence | null;
  onConfirmDelete: (daySequenceId: string) => Promise<void>;
  isDeleting?: boolean;
}

const DeleteTimesheetModal = ({
  isOpen,
  onClose,
  daySequence,
  onConfirmDelete,
  isDeleting = false,
}: DeleteTimesheetModalProps) => {
  const getJobById = useJobStore((state) => state.getJobById);
  const job = daySequence?.jobId ? getJobById(daySequence.jobId) : null;

  if (!daySequence) {
    return null;
  }

  const formattedDate = daySequence.dateTime
    ? dayjs(Number(daySequence.dateTime)).format('ddd, D MMM YYYY')
    : 'Date not set';

  return (
    <Transition show={isOpen} as={React.Fragment}>
      <Dialog as="div" className="relative z-60" onClose={onClose}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white px-6 py-8 text-left align-middle shadow-xl transition-all">
                <DialogTitle
                  as="h3"
                  className="text-lg leading-6 font-medium text-gray-900"
                >
                  Delete timesheet
                </DialogTitle>

                <div className="mt-4 space-y-3 text-sm text-gray-600">
                  <p>
                    This permanently removes the timesheet. An activity log
                    entry will record who deleted it, when, and the original
                    details.
                  </p>
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-gray-900">
                    <p>
                      <span className="text-gray-600">Date:</span>{' '}
                      {formattedDate}
                    </p>
                    <p>
                      <span className="text-gray-600">Status:</span>{' '}
                      {daySequence.status}
                    </p>
                    <p>
                      <span className="text-gray-600">Logged time:</span>{' '}
                      {formatTimeRangeAndDuration(
                        daySequence.clockInTime,
                        daySequence.clockOutTime,
                        job,
                      )}
                    </p>
                    <p>
                      <span className="text-gray-600">Break:</span>{' '}
                      {formatBreakTime(daySequence.breaks)}
                    </p>
                  </div>
                </div>

                <div className="mt-6 flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onClose}
                    disabled={isDeleting}
                    className="!w-auto !rounded-md !py-2 !text-sm !font-medium"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    onClick={async () => {
                      await onConfirmDelete(daySequence.id);
                    }}
                    disabled={isDeleting}
                    className="!w-auto !rounded-md !bg-red-600 !py-2 !text-sm !font-medium hover:!bg-red-700"
                  >
                    {isDeleting ? 'Deleting...' : 'Delete timesheet'}
                  </Button>
                </div>
              </DialogPanel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default DeleteTimesheetModal;
